<template>
	<el-dialog
		:modelValue="open"
		@update:modelValue="$emit('update:open', $event)"
		:title="texts.mediaDescription.title"
		:before-close="handleBeforeClose"
		class="dialog--mediadescription"
		width="85%"
	>
		<el-row :gutter="32">
			<el-col :xs="24" :sm="12">
				<div class="mediadescription__media">
					<div class="mediadescription__preview">
						<img v-if="isImage" :src="file.url" :alt="file.fileName" />
						<video v-else-if="isVideo" :src="file.url" controls preload="metadata" playsinline></video>
						<a v-else :href="showUrl" target="_blank" class="mediadescription__pdf" title="Datei in neuem Tab öffnen">
							<PdfIcon />
							<span>{{ file.fileName }}</span>
						</a>
					</div>
					<div v-if="canEdit" class="mediadescription__action">
						<ButtonFlat variant="ai" :loading="generating" @buttonclick="generate">
							<template #icon><AiSparkIcon /></template>
							{{ texts.mediaDescription.generate }}
						</ButtonFlat>
					</div>
				</div>
			</el-col>

			<el-col :xs="24" :sm="12">
				<div class="mediadescription__panel">
					<el-alert v-if="alertMsg" :title="alertMsg" type="error" effect="dark" :closable="false" show-icon class="mediadescription__alert" />

					<el-input
						v-if="canEdit"
						v-model="description"
						type="textarea"
						:rows="1"
						resize="none"
						:disabled="generating"
						:placeholder="generating ? texts.mediaDescription.generating : texts.mediaDescription.placeholder"
						class="mediadescription__input"
					/>
					<div v-else class="mediadescription__readonly">{{ description }}</div>

					<p v-if="canEdit" class="mediadescription__hint">
						<strong>{{ texts.mediaDescription.hintLabel }}</strong> {{ texts.mediaDescription.hint }}
					</p>
				</div>
			</el-col>
		</el-row>

		<template #footer>
			<div class="mediadescription__footer">
				<template v-if="canEdit">
					<ButtonFlat variant="red" :loading="saving" @buttonclick="save">Speichern</ButtonFlat>
					<ButtonFlat variant="outline" @buttonclick="close">Abbrechen</ButtonFlat>
				</template>
				<ButtonFlat v-else variant="outline" @buttonclick="close">Schließen</ButtonFlat>
			</div>
		</template>
	</el-dialog>

	<!-- Rueckfragen im Layout der bestehenden Bestaetigungsdialoge (vgl. DialogeLeavePageCaseErr) -->
	<el-dialog :modelValue="confirm.open" @update:modelValue="confirm.open = $event" @closed="resolveConfirm(false)" :title="texts.mediaDescription.title" align-center>
		<el-alert :title="confirm.message" type="error" effect="dark" :closable="false" show-icon />

		<template #footer>
			<div class="button-panel">
				<TheButton type="primary" @buttonclick="resolveConfirm(true)">{{ confirm.confirmLabel }}</TheButton>
				<TheButton type="default" @buttonclick="resolveConfirm(false)">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ElMessage } from 'element-plus';
import ButtonFlat from '../buttons/ButtonFlat.vue';
import TheButton from '../TheButton.vue';
import AiSparkIcon from '@/assets/icons/AiSparkIcon.vue';
import PdfIcon from '@/assets/icons/PdfIcon.vue';
import { texts } from '@/utils/texts';
import { FileResponse, MediaDescriptionResult, MediaDescriptionSource } from '@/types/types';
import { generateMediaDescription, saveMediaDescription } from '@/requests/mediaDescriptions';
import { getShowUrl, isImage, isVideo } from '@/utils/fileHandling/files';

export default defineComponent({
	name: 'DialogMediaDescription',
	components: {
		ButtonFlat,
		TheButton,
		AiSparkIcon,
		PdfIcon,
	},
	props: {
		open: {
			type: Boolean,
			default: false,
		},
		file: {
			type: Object as PropType<FileResponse>,
			required: true,
		},
		canEdit: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['update:open', 'saved'],
	data() {
		return {
			description: '' as string,
			source: null as MediaDescriptionSource | null,
			generating: false,
			saving: false,
			alertMsg: '',
			confirm: {
				open: false,
				message: '',
				confirmLabel: 'Fortfahren',
			},
			// Bewusst ausserhalb der Vorlage: haelt den Ausgang der offenen Rueckfrage
			confirmResolve: null as ((confirmed: boolean) => void) | null,
		};
	},
	computed: {
		texts() {
			return texts;
		},
		isImage(): boolean {
			return isImage(this.file);
		},
		isVideo(): boolean {
			return isVideo(this.file);
		},
		showUrl(): string {
			return getShowUrl({ file: this.file });
		},
		isDirty(): boolean {
			return this.description !== (this.file.description ?? '');
		},
	},
	methods: {
		askConfirm(message: string, confirmLabel: string): Promise<boolean> {
			this.confirm = { open: true, message, confirmLabel };
			return new Promise<boolean>((resolve) => {
				this.confirmResolve = resolve;
			});
		},
		resolveConfirm(confirmed: boolean) {
			this.confirm.open = false;
			const resolve = this.confirmResolve;
			this.confirmResolve = null;
			if (resolve) resolve(confirmed);
		},
		async generate() {
			if (this.description.trim().length > 0) {
				const confirmed = await this.askConfirm(this.texts.mediaDescription.overwriteConfirm, 'Ersetzen');
				if (!confirmed) return;
			}
			this.generating = true;
			this.alertMsg = '';

			const result = await generateMediaDescription(this.file.id);

			if (!result.success) {
				this.alertMsg = this.texts.mediaDescription.generateFailed;
			} else if (result.data.result === MediaDescriptionResult.NotPossible) {
				this.alertMsg = this.texts.mediaDescription.notPossible;
				this.description = '';
			} else {
				this.description = result.data.description ?? '';
				this.source = result.data.source ?? null;
			}
			this.generating = false;
		},
		async save() {
			this.saving = true;
			const result = await saveMediaDescription({
				fileId: this.file.id,
				description: this.description,
				source: this.source,
			});
			this.saving = false;

			if (!result.success) {
				this.alertMsg = this.texts.mediaDescription.saveFailed;
				return;
			}
			ElMessage({ message: this.texts.mediaDescription.saveSuccess, type: 'success' });
			// Der Elternteil haelt die Dateiliste — er aktualisiert den Eintrag, statt den Fall neu zu laden
			this.$emit('saved', { fileId: this.file.id, description: this.description, source: this.source });
			this.$emit('update:open', false);
		},
		async handleBeforeClose(done: () => void) {
			if (this.canEdit && this.isDirty) {
				const confirmed = await this.askConfirm(this.texts.mediaDescription.discardConfirm, 'Verwerfen');
				if (!confirmed) return;
			}
			done();
		},
		close() {
			this.handleBeforeClose(() => this.$emit('update:open', false));
		},
		reset() {
			this.description = this.file.description ?? '';
			this.source = this.file.descriptionSource ?? null;
			this.alertMsg = '';
		},
	},
	watch: {
		open(isOpen: boolean) {
			if (isOpen) this.reset();
		},
	},
	mounted() {
		this.reset();
	},
});
</script>

<style lang="scss" scoped>
@use '../../scss/vars.scss' as *;

/* Masse aus Figma-Node 56:600: Medienpanel 390px, darunter der Aktionsstreifen 69px
   (37px Button mit je 16px Luft), rechts das Textfeld 389px, 16px Abstand, Hinweis 54px.
   Beide Spalten sind damit 459px hoch und schliessen unten gemeinsam ab. */
$panel-media-height: 390px;
$panel-action-height: 69px;
$panel-gap: 16px;

.mediadescription__media,
.mediadescription__panel {
	/* Ohne globalen Reset gilt content-box — sonst sprengen die Innenabstaende die Figma-Hoehen */
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	height: $panel-media-height + $panel-action-height;
}

.mediadescription__media {
	background-color: var(--el-color-lightgrey-1);
	border-radius: 6px;
	overflow: hidden;
}

.mediadescription__preview {
	box-sizing: border-box;
	flex: 0 0 $panel-media-height;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 0;
	padding: 16px;

	img,
	video {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}
}

.mediadescription__pdf {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.75rem;
	color: var(--el-color-primary);
	text-align: center;

	svg {
		width: 68px;
		height: 79px;
	}
}

.mediadescription__action {
	box-sizing: border-box;
	/* Streifen darf nicht schrumpfen, sonst verliert der Button seine 16px nach unten */
	flex: 0 0 $panel-action-height;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: $panel-gap 0;
}

.mediadescription__panel {
	gap: $panel-gap;
}

/* Die Meldung verdraengt das Textfeld statt die Spaltenhoehe zu sprengen */
.mediadescription__alert {
	flex: 0 0 auto;
}

.mediadescription__input {
	flex: 1 1 auto;
	display: block;
	min-height: 0;

	:deep(.el-textarea__inner) {
		box-sizing: border-box;
		height: 100%;
		padding: 16px;
		background-color: var(--el-color-lightgrey-1);
		border-radius: 6px;
		box-shadow: none;
		color: var(--el-color-primary);
		font-size: 16px;
		line-height: 1.3;

		&::placeholder {
			font-style: italic;
			color: var(--el-color-primary);
		}

		&:focus {
			box-shadow: 0 0 0 1px var(--el-color-lightgrey-2) inset;
		}
	}
}

.mediadescription__readonly {
	box-sizing: border-box;
	flex: 1 1 auto;
	min-height: 0;
	padding: 16px;
	background-color: var(--el-color-lightgrey-1);
	border-radius: 6px;
	overflow-y: auto;
	font-size: 16px;
	line-height: 1.3;
	white-space: pre-wrap;
}

.mediadescription__hint {
	flex: 0 0 auto;
	margin: 0;
	font-size: 14px;
	line-height: 1.3;
	color: var(--el-color-primary);
}

.mediadescription__footer {
	display: flex;
	gap: 12px;
}

/* mobile styles */
@media screen and (max-width: $mobile_maxwidth) {
	.mediadescription__media,
	.mediadescription__panel {
		height: auto;
	}

	.mediadescription__preview {
		flex: 0 0 auto;
		max-height: 260px;
	}

	.mediadescription__input :deep(.el-textarea__inner),
	.mediadescription__readonly {
		height: 240px;
		flex: 0 0 240px;
		margin-top: 16px;
	}
}
</style>
